import ReactDOM from 'react-dom';
import { useContext, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { PopOutAudioContext } from '../../pop-out-audio';
import MiniAudioPlayer from '../audio-player/audio-player';
import { AudioList } from '../audio-list/audio-list';
import { PlaylistAudioItem, PopOutAudioContextValue } from '../../types';
import List from '../../../../general/icon/components/list';
import Close from '../../../../general/icon/components/close';
import ChevronDown from '../../../../general/icon/components/chevron-down';
import { useOutsideClick } from '../../../../../global/hooks/use-outside-click';

import { ErrorMessage } from '../error-message/error-message';
import { getPlaylistHeightForMobile } from './mini-player.helpers';

import styles from './mini-player.module.scss';
import {
  PlayStates,
  VisibilityStates,
} from '../../../pop-out-media-manager/types';
import { OverlayWrapper } from '../../../../layouts/overlay-manager/components/overlay-wrapper/overlay-wrapper';
import { indexNames } from '../../../../layouts/overlay-manager/overlay-manager.constants';
import type { OverlayManagerContextValue } from '../../../../layouts/overlay-manager/types';
import { OverlayManagerContext } from '../../../../layouts/overlay-manager/overlay-manager';

type MiniPlayerProps = {
  playlistTitle?: string;
  isMobile: boolean;
};

type Ref<ElementType> = {
  current: ElementType | null;
};

export const MiniPlayer = ({ playlistTitle, isMobile }: MiniPlayerProps) => {
  const [userDidScrollPlaylist, setUserDidScrollPlaylist] =
    useState<boolean>(false);
  const [playlistOpen, setPlaylistOpen] = useState<boolean>(false);
  const [playlistVisibilityHidden, setPlaylistVisibilityHidden] =
    useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);

  const containerRef: Ref<HTMLDivElement> = useRef(null);
  const portalRef: Ref<HTMLDivElement> = useRef(null);
  const portalParentRef: Ref<HTMLDivElement> = useRef(null);
  const activePlaylistItemRef: Ref<HTMLLIElement> = useRef(null);
  const playlistElementRef: Ref<HTMLDivElement> = useRef(null);
  const playlistContainerRef: Ref<HTMLDivElement> = useRef(null);
  const playerContainerRef: Ref<HTMLDivElement> = useRef(null);
  const {
    playlist,
    getCurrentlyActive,
    playNextAudio,
    pauseAudio,
    playAudio,
    setAllInactive,
    setAudioPlayerInstance,
    setAudioPlayerContainerHeight,
    audioPlayerPortalOverrideParent,
    audioPlayerPortalOverrideTrigger,
    setAudioPlayerPortalOverrideTrigger,
    popOutVisibilityState,
  } = useContext<PopOutAudioContextValue>(PopOutAudioContext);
  useOutsideClick(containerRef, () => {
    setPlaylistOpen(false);
  });

  const { updateDimensions, updateVisibility } =
    useContext<OverlayManagerContextValue>(OverlayManagerContext);

  const currentAudio =
    getCurrentlyActive() ||
    ({
      state: {},
      srcUrl: '',
      type: '',
      title: '',
      itemId: '',
    } as PlaylistAudioItem);

  const updatePlayerHeight = () => {
    if (!playerContainerRef.current) {
      return;
    }
    const { height } = playerContainerRef.current.getBoundingClientRect();
    setAudioPlayerContainerHeight(height);
  };

  const setPlaylistHeight = (recalculate: boolean | null = false) => {
    const haveAllRefs =
      playlistContainerRef.current && playerContainerRef.current;

    if (haveAllRefs && isMobile) {
      /**
       * on mobile we want the playlist to expand to the full height of the screen
       * so we calculate this value here
       */
      if (playlistOpen || recalculate) {
        const { height: playerHeight } = (
          playerContainerRef.current as HTMLElement
        ).getBoundingClientRect();
        const height = getPlaylistHeightForMobile(
          playerHeight,
          window.innerHeight,
        );

        (playlistContainerRef.current as HTMLElement).style.height =
          `${height}px`;
      } else {
        (playlistContainerRef.current as HTMLElement).style.height = '';
      }
    }
  };

  useEffect(() => {
    setShowError(false);
  }, [currentAudio.itemId]);

  useEffect(() => {
    const onResize = debounce(() => {
      if (playlistOpen) {
        setPlaylistHeight(true);
        updateDimensions(overlayConfig);
      }
    }, 500);

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [playlistOpen]);

  useEffect(() => {
    updateVisibility({
      ...overlayConfig,
      visibility: popOutVisibilityState,
    });
  }, [popOutVisibilityState]);

  useEffect(setPlaylistHeight, [playlistOpen]);

  useEffect(() => {
    if (audioPlayerPortalOverrideTrigger && portalRef.current) {
      if (audioPlayerPortalOverrideParent) {
        // move audio player into modal to enable keyboard navigation
        audioPlayerPortalOverrideParent.appendChild(portalRef.current);
        setAudioPlayerPortalOverrideTrigger(false);
      } else if (portalParentRef.current) {
        // move audio player back to default position on main page
        portalParentRef.current.appendChild(portalRef.current);
        setAudioPlayerPortalOverrideTrigger(false);
      }
    }
  }, [audioPlayerPortalOverrideTrigger]);

  const togglePlaylistOpen = () => {
    setPlaylistOpen(isOpen => !isOpen);
    setPlaylistVisibilityHidden(false);
  };

  const onClose = () => {
    updateVisibility({
      ...overlayConfig,
      visibility: VisibilityStates.Hidden,
    });
  };

  const onContainerTransitionEnd = visibility => {
    switch (visibility) {
      case VisibilityStates.Hidden:
        setAllInactive();
        setPlaylistOpen(false);
        break;
      case VisibilityStates.Visible:
        updateDimensions(overlayConfig);
        break;
    }
  };

  const scrollToActiveItem = () => {
    if (
      !userDidScrollPlaylist &&
      activePlaylistItemRef.current &&
      playlistElementRef.current &&
      playlistElementRef.current.scroll
    ) {
      playlistElementRef.current.scroll({
        top: activePlaylistItemRef.current.offsetTop - 40,
        behavior: 'smooth',
      });
    }
  };

  const onPlaylistOpenTransitionEnd = () => {
    if (playlistOpen) {
      setAudioPlayerContainerHeight(0);
      scrollToActiveItem();
    } else {
      updatePlayerHeight();
      setUserDidScrollPlaylist(false);
    }

    updateDimensions(overlayConfig);
  };

  const onUserInteracted = () => {
    setUserDidScrollPlaylist(true);
  };

  const onVolumeClicked = () => {
    setPlaylistOpen(false);
  };

  const onPlayerReady = player => {
    if (player) {
      setAudioPlayerInstance(player);
    }
  };

  const onError = () => {
    setShowError(true);
  };

  const onErrorTransitionEnd = () => {
    updateDimensions(overlayConfig);
    updatePlayerHeight();
  };

  const onContainerKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (evt.key === 'Escape') {
      onClose();
    }
  };

  // audio player is rendered in a portal
  // this allows its parent div to be moved into a modal dialog
  // to support keyboard navigation
  // and them moved back to the main page after the modal closes
  const renderContent = () => {
    // to render the component in tests, there needs to be an existing element for the React portal
    // this will be created and attached to the DOM by the test
    // so in deployment it doesn't exist and nothing is rendered until the portalRef exists
    let portalElm = document.getElementById(
      'audio-player-root-for-testing-only',
    );

    if (portalRef.current) {
      portalElm = portalRef.current; // avoids flow error in createPortal
    }

    if (portalElm) {
      return ReactDOM.createPortal(
        <OverlayWrapper
          onTransitionEnd={onContainerTransitionEnd}
          indexName={indexNames.popOutAudioPlayer}
          {...overlayConfig}
        >
          <div
            onTransitionEnd={onContainerTransitionEnd}
            ref={containerRef}
            onKeyDown={onContainerKeyDown}
            role='region'
            aria-label={polyglot.t('audio_player.audio_player')}
            className={classNames(
              styles['mini-player'],
              styles['mini-player--fixed'],
              playlistOpen && styles['mini-player--playlist-open'],
              showError && styles['mini-player--error-open'],
              currentAudio.srcUrl && styles['mini-player--active'],
              audioPlayerPortalOverrideParent &&
                styles['mini-player--detached'],
            )}
          >
            <div
              className={styles['mini-player__wrapper']}
              ref={playerContainerRef}
            >
              <div className={styles['mini-player__player-container']}>
                <div className={styles['mini-player__player']}>
                  <MiniAudioPlayer
                    title={currentAudio.title}
                    playing={
                      currentAudio.state.playState === PlayStates.Playing
                    }
                    onAudioEnded={playNextAudio}
                    onAudioPaused={() =>
                      pauseAudio(getCurrentlyActive()?.itemId)
                    }
                    onAudioPlayed={() =>
                      playAudio(getCurrentlyActive()?.itemId)
                    }
                    type={currentAudio.type}
                    url={currentAudio.srcUrl}
                    onVolumeClicked={onVolumeClicked}
                    onPlayerReady={onPlayerReady}
                    onError={onError}
                    onRequestClose={onClose}
                  />
                </div>
                <div className={styles['mini-player__buttons-container']}>
                  <button
                    data-hook='mini-audio-player-toggle-playlist'
                    onMouseDown={e => e.preventDefault()}
                    onClick={togglePlaylistOpen}
                    className={classNames(
                      styles['mini-player__button'],
                      styles['mini-player__button--playlist'],
                    )}
                  >
                    <List />
                  </button>
                </div>
                <div className={styles['mini-player__close-container']}>
                  <button
                    data-hook='mini-audio-player-close'
                    onMouseDown={e => e.preventDefault()}
                    className={styles['mini-player__close-icon']}
                    onClick={onClose}
                  >
                    <Close />
                  </button>
                </div>
              </div>
              <div
                className={styles['mini-player__error']}
                onTransitionEnd={onErrorTransitionEnd}
                aria-live='assertive'
                aria-hidden={!showError}
              >
                <ErrorMessage
                  message={polyglot.t('audio_player.media_error')}
                />
              </div>
            </div>
            <div
              ref={playlistContainerRef}
              onTransitionEnd={onPlaylistOpenTransitionEnd}
              className={classNames(
                styles['mini-player__playlist-container'],
                playlistVisibilityHidden &&
                  styles['mini-player__playlist-container--hidden'],
              )}
              aria-hidden={!playlistOpen}
            >
              {!!playlistTitle && (
                <h4
                  onClick={togglePlaylistOpen}
                  className={styles['mini-player__playlist-header']}
                >
                  {playlistTitle}
                  <span className={styles['mini-player__playlist-header-icon']}>
                    <ChevronDown />
                  </span>
                </h4>
              )}
              <div
                ref={playlistElementRef}
                onWheel={onUserInteracted}
                onTouchStart={onUserInteracted}
                className={styles['mini-player__playlist-items-wrapper']}
              >
                <AudioList
                  ref={activePlaylistItemRef}
                  audios={playlist}
                  addToProvider={false}
                />
              </div>
            </div>
          </div>
        </OverlayWrapper>,
        portalElm,
      );
    }
  };

  return (
    <div ref={portalParentRef}>
      <div ref={portalRef}>{renderContent()}</div>
    </div>
  );
};

MiniPlayer.displayName = 'MiniAudioPlayer';

const overlayConfig = {
  componentName: MiniPlayer.displayName,
} as const;
